import type { AppDispatch } from "_shared/_stores/store-types";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { bookmarksApi } from "marketplace/bookmarks/_api/bookmark-api";
import {
  getHasIndividualBookmarkStatusFailed,
  getIndividualBookmarkByIndividualId,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import type { BookmarkedIndividualJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

/** The most ids that `POST marketplace/individual-bookmarks/by-individual-ids` accepts in one request. */
export const INDIVIDUAL_BOOKMARK_STATUS_BATCH_SIZE = 100;

const pendingIndividualIds = new Set<string>();
const requestedIndividualIds = new Set<string>();
let isFlushScheduled = false;

/**
 * Asks the server whether the session user has bookmarked this person. The ids that components
 * ask for in the same tick go in one bulk request: a marketplace results page shows a bookmark
 * button on each card, and one request for each card would be an N+1 across the network.
 * An id is asked once until its request ends; the answer goes into the bookmarks slice.
 */
export function requestIndividualBookmarkStatus(dispatch: AppDispatch, individualId: Guid): void {
  if (pendingIndividualIds.has(individualId.value) || requestedIndividualIds.has(individualId.value)) return;
  pendingIndividualIds.add(individualId.value);
  if (isFlushScheduled) return;
  isFlushScheduled = true;
  setTimeout(() => flushIndividualBookmarkStatusRequests(dispatch), 0);
}

function flushIndividualBookmarkStatusRequests(dispatch: AppDispatch): void {
  isFlushScheduled = false;
  const individualIds = [...pendingIndividualIds];
  pendingIndividualIds.clear();

  for (let start = 0; start < individualIds.length; start += INDIVIDUAL_BOOKMARK_STATUS_BATCH_SIZE) {
    const batch = individualIds.slice(start, start + INDIVIDUAL_BOOKMARK_STATUS_BATCH_SIZE);
    for (const individualId of batch) requestedIndividualIds.add(individualId);
    const request = dispatch(
      bookmarksApi.endpoints.getIndividualBookmarksByIndividualIds.initiate(
        { individualIds: batch.map((individualId) => new Guid(individualId)) },
        // Asked only when the slice has no answer, so a cached reply is never the one wanted. RTK
        // Query keeps the last reply for these ids a minute after the request ends, and without
        // this would return it unsent.
        { forceRefetch: true },
      ),
    );
    void request.finally(() => {
      for (const individualId of batch) requestedIndividualIds.delete(individualId);
      request.unsubscribe();
    });
  }
}

export type IndividualBookmarkStatus = {
  /** The bookmark, null when there is none, or undefined while the answer loads or after it failed. */
  bookmark: BookmarkedIndividualJSON | null | undefined;
  /** The ask failed. It is not repeated by itself, so that an outage is not asked about in a loop. */
  hasFailed: boolean;
  /** Asks again after a failure. */
  retry: () => void;
};

/**
 * The session user's bookmark of this person. Pass no id, or `skip`, to ask nothing.
 */
export default function useIndividualBookmarkStatus(
  individualId?: Guid,
  options?: { skip?: boolean },
): IndividualBookmarkStatus {
  const dispatch = useAttorneyHubDispatch();
  const bookmark = useSelector(getIndividualBookmarkByIndividualId(individualId));
  const failed = useSelector(getHasIndividualBookmarkStatusFailed(individualId));
  const skip = options?.skip ?? false;
  const individualIdValue = individualId?.value;

  useEffect(() => {
    if (skip || !individualIdValue || bookmark !== undefined || failed) return;
    requestIndividualBookmarkStatus(dispatch, new Guid(individualIdValue));
  }, [bookmark, dispatch, failed, individualIdValue, skip]);

  const retry = useCallback(() => {
    if (skip || !individualIdValue) return;
    requestIndividualBookmarkStatus(dispatch, new Guid(individualIdValue));
  }, [dispatch, individualIdValue, skip]);

  if (skip) return { bookmark: undefined, hasFailed: false, retry };
  return { bookmark, hasFailed: failed, retry };
}
